import Layout from '../components/base/layout/layout';
import { getObject } from './ObjectUtil';
import { Constants, getBackendUrl } from './Constants';
import { Templates } from '../components/base/templates';
import {
  categoriesProps,
  latestProps,
  popularDownloadsBlockProps,
  popularDownloadsProps,
  productFamilyCategoriesProps,
  Resources,
} from '../pageLayout/resources';
import { pressReleasesCategoriesProps } from '../components/press-releases/press-releases';
import {
  createFilterData,
  createFilterDataForResources,
  createFilterDataPartners,
  createFilterDataProductFamily,
  createFilterPressReleases,
  fetchData,
  fetchDataApiV2,
  getAlgoliaDatasetValue,
  getAlgoliaIndex,
  getValueForPopularAndRelatedBlock,
  isBlockPopular,
  isBlockRelated,
  marketoOptions as algoliaOptions,
} from './DataUtil';
import {
  getPageFromQuery,
  getSearchFromQuery,
  hasInvalidPageParam,
} from './PaginationUtil';
import { useEffect, useState } from 'react';
import { GlobalSearchPage } from '../pageLayout/global-search-page';
import { IndividualProductTemplate } from '../components/base/individual-product-templates';
import dynamic from 'next/dynamic';
import { ProductFamilyTemplate } from '../components/base/product-family-template';
import Errors from '../pageLayout/errors';

const Home = dynamic(() => import('../pageLayout/home'));
const Region = dynamic(() => import('../components/base/region'));
const Metro = dynamic(() => import('../components/base/metro'));
const Facility = dynamic(() => import('../components/base/facility'));
const Locations = dynamic(() => import('../components/base/location'));
const PressReleases = dynamic(
  () => import('../components/press-releases/press-releases')
);

export function getPageHtml(props) {
  return (
    <Layout
      contentType={props?.data?.content_type}
      urlAlias={props?.urlAlias}
      data={props?.data}
      store={props?.store}
      lang={props?.lang}
      meta={getObject(props, 'meta_tags')}
      menuData={props.menuData ? props.menuData : props.props?.menuData}
      // menuData={data.menuData}
      countryData={props?.countryData ? props.countryData : null}
      footerData={props ? props.footerData : undefined}
      globalMetrics={props.globalMetrics}
      endpoint={props.endpoint}
      languageSelectorData={
        props.languageSelectorData ? props.languageSelectorData : []
      }
    >
      {getHtmlForContentType(props)}
    </Layout>
  );
}

const ipHeaders = (clientIp) => {
  const ip = typeof clientIp === 'string' ? clientIp : '';
  if (!ip) {
    console.log('client ip not picked up');
  }
  const val = ip || 'no-ip';
  return { 'X-Real-IP': val, 'X-Forwarded-For': val };
};

// NEW GET PAGE PROPS

export async function getPageProps(
  context,
  store,
  adminCookie,
  clientIp,
  lang
) {
  let host = '';

  if (store) {
    host = store.getState().host.host;
  } else {
    const { req } = context;
    if (req) {
      host = req.headers.host;
    }

    lang = lang;
  }

  const slug = context.params?.slug || [];
  const isHome = context.params?.isHome ?? slug.length === 0;

  let urlAlias = '';

  if (isHome) {
    urlAlias = 'home';
  } else {
    urlAlias = slug.join('/');
  }

  // Detect preview from the raw request URL as well as query/slug. The CMS
  // Preview button can send the flag as `?dlrpreview`, as `&dlrpreview`
  // appended to a locale-prefixed path, or as part of the slug — depending on
  // routing. Checking req.url covers every format so the draft is fetched with
  // `&dlrpreview` instead of falling back to Drupal's redirect-to-home.
  const rawReqUrl = context?.req?.url || '';
  const isPreview =
    'dlrpreview' in (context.query || {}) ||
    urlAlias.includes('dlrpreview') ||
    rawReqUrl.includes('dlrpreview');

  const cleanAlias = urlAlias.split('&')[0];

  urlAlias = cleanAlias.split('/').map(encodeURIComponent).join('/');

  const endpoint = getBackendUrl(host);
  let urlAliasPath = `url-aliasv2?alias=/${urlAlias}&langcode=${lang}`;

  if (isPreview) {
    urlAliasPath += '&dlrpreview';
  }

  const headers = ipHeaders(clientIp);

  let data = await fetchDataApiV2(endpoint, urlAliasPath, headers);

  // Never follow a CMS redirect (e.g. redirect-to-home) for a preview request —
  // the editor must stay on the draft. With isPreview correctly detected the CMS
  // returns the draft directly, but this guard prevents any regression.

  if (data?.redirect_page_url && !isPreview) {
    let redirectDomain = '/' + lang;
    if (
      lang == 'en' ||
      lang == 'dk' ||
      lang == 'be' ||
      lang == 'se' ||
      lang == 'at' ||
      lang == 'ch' ||
      lang == 'en-gb' ||
      lang == 'ie' ||
      lang == 'el' ||
      lang == 'nl' ||
      lang == 'asia'
    ) {
      redirectDomain = '';
    }
    if (lang == 'zh-hans') {
      redirectDomain = '/zh';
    }
    if (data?.redirect_domain === 'global') {
      redirectDomain = '/';
    }
    return {
      redirect: {
        destination: redirectDomain + data.redirect_page_url,
        statusCode: parseInt(data.redirect_page_status || 301, 10),
      },
    };
  }

  if (data?.status === 404) {
    data = await fetchDataApiV2(
      endpoint,
      `url-aliasv2?alias=/404&langcode=${lang}`,
      headers
    );
  } else if (data?.status) {
    throw new Error(`Error with status code ${data.status}`);
  }

  // Unpublished pages are served as the CMS /404 page, which has
  // field_error_codes '1', so pages/[[...slug]].js answers with a real 404.
  if (
    data?.published === '0' &&
    !isPreview &&
    (typeof adminCookie === 'undefined' || adminCookie === '{}')
  ) {
    data = await fetchDataApiV2(
      endpoint,
      `url-aliasv2?alias=/404&langcode=${lang}`,
      headers
    );
  }

  const [
    menuData,
    countryData,
    dataCenters,
    footerData,
    globalMetrics,
    languageSelectorData,
  ] = await Promise.all([
    fetchData(endpoint, `menu?lang=${lang}`),
    fetchData(endpoint, `country?lang=${lang}`),
    fetchData(endpoint, `data-center-locations-filters?lang=${lang}`),
    fetchData(endpoint, getFooterEndpoint(lang)),
    fetchDataApiV2(
      endpoint,
      'url-aliasv2?alias=/global-metrics-api&langcode=' + lang,
      headers
    ),
    fetchData(endpoint, `language-selector`),
  ]);

  return getPropsForContentType(
    data,
    menuData,
    lang,
    data?.content_type || '',
    store,
    footerData,
    urlAlias,
    globalMetrics,
    urlAliasPath,
    host,
    endpoint,
    countryData,
    dataCenters,
    context.query,
    languageSelectorData
  );
}

function getFooterEndpoint(lang) {
  const langEndpoints = {
    en: 'footer?lang=en',
    'en-gb': 'footer-uk',
    de: 'footer-de',
    fr: 'footer-fr',
    nl: 'footer-nl',
    'zh-hans': 'footer-zh-hans',
    ko: 'footer-ko',
    asia: 'footer-asia',
    es: 'footer-es',
    at: 'footer-at',
    ch: 'footer-ch',
    se: 'footer-se',
    dk: 'footer-dk',
    ie: 'footer-ie',
    bg: 'footer-bg',
    // hr: 'footer-hr',
    be: 'footer-be',
    el: 'footer-el',
    it: 'footer-it',
  };

  return langEndpoints[lang] || 'footer?lang=en';
}

function getHtmlForContentType(data) {
  // const [width, setWidth] = useState(typeof document !== 'undefined' ? document.getElementsByTagName('main')[0]?.offsetWidth : 0)
  const [width, setWidth] = useState(0);

  useEffect(() => {
    typeof document !== 'undefined' &&
      setWidth(document.getElementsByTagName('main')[0]?.offsetWidth);
  }, []);

  useEffect(() => {
    resize();
  });

  const resize = () => {
    window.onresize = function (event) {
      setWidth(
        typeof document !== 'undefined'
          ? document.getElementsByTagName('main')[0]?.offsetWidth
          : 0
      );
    };
  };
  if (data.type === 'location') {
    return (
      <Facility
        tags={data.tags}
        related_content={data.related_content}
        popular_downloads_posts={data.popular_downloads_posts}
        width={width}
        global={data.globalMetrics}
        lang={data.lang}
        data={data.data}
        type={data.type}
      />
    );
  } else if (data.type === 'home') {
    return (
      <Home
        related_content={data.related_content}
        popular_downloads_posts={data.popular_downloads_posts}
        global={data.globalMetrics}
        data={data.data}
        lang={data.lang}
        width={width}
        type={data.type}
      />
    );
  } else if (data.type === 'resources_page') {
    return (
      <Resources
        posts={data.resource_popular_downloads_posts}
        global={data.globalMetrics}
        data={data.data}
        type={data.type}
        width={width}
        lang={data.lang}
      />
    );
  } else if (data.type === 'global_search_page') {
    return (
      <GlobalSearchPage
        globalMetrics={data.globalMetrics}
        data={data.data}
        type={data.type}
        width={width}
        lang={data.lang}
      />
    );
  } else if (
    data.type === 'gated_template' ||
    data.type === 'media_template' ||
    data.type === 'solution_template' ||
    data.type === 'non_gated_template' ||
    data.type === 'press_release' ||
    data.type === 'events' ||
    data.type === 'case_study_dynamic' ||
    data.type === 'author' ||
    (data.type === 'freestyle_template' &&
      data.data.field_page_type[0]?.value === 'Articles')
  ) {
    return (
      <Templates
        tags={data.tags}
        related_content={data.related_content}
        popular_downloads_posts={data.popular_downloads_posts}
        width={width}
        global={data.globalMetrics}
        lang={data.lang}
        data={data.data}
        type={data.type}
      />
    );
  } else if (data.type === 'metro') {
    return (
      <Metro
        metros={data.metros}
        global={data.globalMetrics}
        tags={data.tags}
        related_content={data.related_content}
        popular_downloads_posts={data.popular_downloads_posts}
        data={data.data}
        lang={data.lang}
        width={width}
      />
    );
  } else if (
    data.type === 'individual_product_template' ||
    data.type === 'content_template' ||
    data.type === 'newsroom' ||
    data.type === 'info_template' ||
    // data.type === 'freestyle_template' ||
    data.type === 'partner' ||
    data.type === 'blog_main_page' ||
    (data.type === 'freestyle_template' &&
      data.data.field_page_type[0]?.value !== 'Articles')
  ) {
    //TODO: console.log("here data", data);
    return (
      <IndividualProductTemplate
        related_content={data.related_content}
        popular_downloads_posts={data.popular_downloads_posts}
        global={data.globalMetrics}
        data={data.data}
        lang={data.lang}
        width={width}
        type={data.type}
        contentType={data?.content_type}
      />
    );
  } else if (data.type === 'product_family_template') {
    return (
      <ProductFamilyTemplate
        related_content={data.related_content}
        popular_downloads_posts={data.popular_downloads_posts}
        global={data.globalMetrics}
        data={data.data}
        lang={data.lang}
        width={width}
        type={data.type}
      />
    );
  } else if (data.type === 'region') {
    return (
      <Region
        metros={data.metros}
        tags={data.tags}
        related_content={data.related_content}
        popular_downloads_posts={data.popular_downloads_posts}
        global={data.globalMetrics}
        data={data.data}
        lang={data.lang}
        width={width}
        dataCenters={data.dataCenters}
      />
    );
  } else if (data.type === 'global_data_centre_locations') {
    return (
      <Locations
        global={data.globalMetrics}
        data={data.data}
        lang={data.lang}
        width={width}
        related_content={data.related_content}
        popular_downloads_posts={data.popular_downloads_posts}
        dataCenters={data.dataCenters}
      />
    );
  } else if (data.type === 'press_releases_page') {
    return (
      <PressReleases
        global={data.globalMetrics}
        data={data.data}
        lang={data.lang}
        width={width}
      />
    );
    // return <PressReleasesPage data={data.data} lang={data.lang} width={width} />
  } else if (data.type === 'error') {
    return (
      <Errors global={data.globalMetrics} data={data.data} lang={data.lang} />
    );
  }
}

async function getPropsForContentType(
  data,
  menuData,
  lang,
  type,
  store,
  footerData,
  urlAlias,
  globalMetrics,
  l,
  host,
  endpoint,
  countryData,
  dataCenters,
  contextQuery,
  languageSelectorData
) {
  let ret = {};
  let metros = null;
  if (
    data?.content_type === 'location' ||
    data?.content_type === 'facility' ||
    data?.content_type === 'global_data_centre_locations' ||
    data?.content_type === 'error'
  ) {
    ret = data;
  } else if (
    data?.content_type === 'metro' ||
    data?.content_type === 'region'
  ) {
    ret = data;
    metros = await new Promise((resolve, reject) => {
      resolve(allMetrosProps(endpoint, lang));
    });
  } else if (data?.content_type === 'blog_flexible') {
    ret = data;
  } else if (data?.content_type === 'resources_page') {
    ret = {
      ...data,
      categories: await new Promise((resolve, reject) => {
        resolve(categoriesProps(endpoint, lang));
      }),
      field_featured_title:
        data.field_featured_content?.length > 0
          ? data.field_featured_content[0]?.featured_content_block_type
              ?.field_title_color
          : null,
      field_filter_resources: data.field_filter_resources,
      field_popular_downloads: contextQuery?.type
        ? null
        : data.field_popular_downloads,
      field_search_resources: data.field_search_resources,
      field_results_label: data?.field_results_label || '',
      field_no_results_text: data?.field_no_results_text || '',
      field_clear_all_text: data?.field_clear_all_text || '',
      field_most_popular_text: data?.field_most_popular_text || '',
      featured: contextQuery?.type
        ? null
        : data.field_featured_content?.length > 0
          ? data.field_featured_content[0]?.featured_content_block_type
              ?.field_featured_posts
          : [],
      latest: contextQuery?.type
        ? null
        : await new Promise((resolve, reject) => {
            resolve(latestProps(endpoint, lang));
          }),
      translang: data.translang,
    };
  } else if (data?.content_type === 'press_releases_page') {
    ret = {
      categories: await new Promise((resolve, reject) => {
        resolve(pressReleasesCategoriesProps(endpoint, lang));
      }),
      ...data,
    };
  } else if (data?.content_type === 'global_search_page') {
    ret = {
      categories: await new Promise((resolve, reject) => {
        resolve(categoriesProps(endpoint, lang));
      }),
      field_filter_resources: data?.field_filter_resources || '',
      field_resources_title: data?.field_resources_title || '',
      field_resources_subtitle: data?.field_resources_subtitle || '',
      field_search_resources: data?.field_search_resources || '',
      field_results_label: data?.field_results_label || '',
      field_no_results_text: data?.field_no_results_text || '',
      field_clear_all_text: data?.field_clear_all_text || '',
      field_most_popular_text: data?.field_most_popular_text || '',
      translang: data.translang,
    };
  } else if (
    data?.content_type === 'gated_template' ||
    data?.content_type === 'media_template' ||
    data?.content_type === 'press_release' ||
    data?.content_type === 'events' ||
    data?.content_type === 'case_study_dynamic' ||
    data?.content_type === 'solution_template' ||
    data?.content_type === 'author' ||
    data?.content_type === 'non_gated_template' ||
    data?.content_type === 'content_template' ||
    data?.content_type === 'info_template' ||
    data?.content_type === 'freestyle_template' ||
    data?.content_type === 'partner' ||
    data?.content_type === 'blog_main_page'
  ) {
    ret = {
      categories: await new Promise((resolve, reject) => {
        resolve(productFamilyCategoriesProps(endpoint, lang));
      }),
      // TO:DO change with partner content type
      partnerCategories: await new Promise((resolve, reject) => {
        resolve(partnerServicesProp(endpoint, lang));
      }),
      partners: await new Promise((resolve, reject) => {
        resolve(allPartners(endpoint, lang));
      }),
      topics: await new Promise((resolve, reject) => {
        resolve(topicsProps(endpoint, lang));
      }),
      contentTags: await new Promise((resolve, reject) => {
        resolve(categoriesProps(endpoint, lang));
      }),
      ...data,
    };
  } else if (
    data?.content_type === 'home' ||
    data?.content_type === 'individual_product_template' ||
    data?.content_type === 'newsroom'
  ) {
    ret = {
      categories: await new Promise((resolve, reject) => {
        resolve(productFamilyCategoriesProps(endpoint, lang));
      }),
      ...data,
    };
  } else if (data?.content_type === 'product_family_template') {
    ret = {
      categories: await new Promise((resolve, reject) => {
        resolve(productFamilyCategoriesProps(endpoint, lang));
      }),
      ...data,
    };
  }
  //  else if (data?.content_type === 'partner') {
  //   ret = {
  //     partners: await new Promise((resolve, reject) => {
  //       resolve(allPartners(endpoint, lang));
  //     }),
  //     ...data,
  //   };
  // }

  // ret.published = data.published
  ret.published = data?.published ? data?.published : false;

  let popularDupes = '';
  let relatedDupes = '';

  let manualPopular = getValueForPopularAndRelatedBlock(
    ret.field_template_blocks,
    1
  )[0]?.field_related_manual_content;
  let popularLimit = getValueForPopularAndRelatedBlock(
    ret.field_template_blocks,
    1
  )[0]?.field_related_content_item_limit[0]?.value;

  let manualRelated = getValueForPopularAndRelatedBlock(
    ret.field_template_blocks,
    0
  )[0]?.field_related_manual_content;
  let relatedLimit = getValueForPopularAndRelatedBlock(
    ret.field_template_blocks,
    0
  )[0]?.field_related_content_item_limit[0]?.value;

  if (typeof manualPopular !== 'undefined') {
    manualPopular.forEach((item, index) => {
      if (index === 0) {
        popularDupes += item?.node_id;
        return;
      }

      popularDupes += ',' + item?.node_id;
    });
  }

  if (popularDupes === '') {
    popularDupes += ret.node_id;
  } else {
    popularDupes += ',' + ret.node_id;
  }

  if (typeof manualRelated !== 'undefined') {
    manualRelated.forEach((item, index) => {
      if (!item) {
        return;
      }
      if (index === 0) {
        relatedDupes += item.node_id;
        return;
      }

      relatedDupes += ',' + item.node_id;
    });
  }

  if (relatedDupes === '') {
    relatedDupes += ret.node_id;
  } else {
    relatedDupes += ',' + ret.node_id;
  }

  if (typeof relatedLimit === 'undefined') {
    relatedLimit = 0;
  }

  if (typeof popularLimit === 'undefined') {
    popularLimit = 0;
  }

  let categories = getAllCategories(
    ret.field_template_category,
    ret.field_template_secondary_categor,
    ret.field_template_tertiary_category
  );

  // Server-render the requested slice of whichever listing this page carries,
  // so the results — and the link to the next page — are in the source HTML.
  //
  // Driven off the blocks actually present rather than off `content_type`: a
  // listing block can be placed on any template, and a new template that reuses
  // one of these blocks is covered without touching this file.
  //
  // Results are hung off `ret` so they reach the block the same way `topics`
  // and `contentTags` already do — the renderers read everything from `data`.
  const listingPage = getPageFromQuery(contextQuery);
  const isAuthorPage = ret?.content_type === 'author';
  // `?page=abc`, `?page=0`, `?page=-1` on an article listing: redirected to
  // the bare URL by the page route rather than served as page one again.
  ret.invalidListingPage =
    (ret?.field_template_blocks || []).some(
      (block) => block && typeof block === 'object' && 'latest_articles_block_type' in block
    ) && hasInvalidPageParam(contextQuery);
  const blockTypes = new Set(
    (ret?.field_template_blocks || [])
      .filter((block) => block && typeof block === 'object')
      .flatMap((block) => Object.keys(block))
  );

  await Promise.all([
    blockTypes.has('latest_articles_block_type')
      ? latestArticlesProps(
          endpoint,
          lang,
          listingPage,
          // An author's page lists only that author's articles. Without the
          // filter here, `?page=N` server-rendered the general Blog pool —
          // other authors' articles, and a full page every time, so the
          // rel="next" link never ran out.
          isAuthorPage ? ret?.node_id : ''
        ).then((result) => {
          ret.initialArticles = result?.articles ?? null;

          if (result) {
            ret.initialArticlesHasMore = result.hasMore;
          }

          if (result) {
            // Past the last page: nothing to list. A 404, rather than an empty
            // page a crawler could keep walking — and whose rel="prev" pointed
            // at another page that does not exist.
            ret.listingOutOfRange =
              listingPage > 0 && result.articles.length === 0;
          }
        })
      : null,
    // The Newsroom listing is its own page type rather than a block — the
    // `press_releases_block_type` block is the small "latest" teaser, which has
    // no pagination of its own.
    type === 'press_releases_page'
      ? pressReleasesListingProps(host, lang, listingPage).then((results) => {
          ret.initialPressReleases = results;
        })
      : null,
    blockTypes.has('partners_cards_block')
      ? partnersListingProps(host, lang, listingPage).then((results) => {
          ret.initialPartners = results;
        })
      : null,
    blockTypes.has('product_family_block_type')
      ? productFamilyListingProps(
          host,
          lang,
          listingPage,
          (ret.field_template_blocks || []).find(
            (block) => block?.product_family_block_type
          )?.product_family_block_type
        ).then((results) => {
          ret.initialProductFamily = results;
        })
      : null,
    type === 'global_search_page'
      ? globalSearchListingProps(
          host,
          lang,
          listingPage,
          getSearchFromQuery(contextQuery)
        ).then((results) => {
          ret.initialGlobalSearch = results;
        })
      : null,
    // The Resources hub is deliberately NOT fetched here. Its result grid is
    // gated behind `results && (filterProps.length || query.length)` — it only
    // appears once the visitor searches or filters, so there is no default
    // paginated series to crawl and a server fetch would be an Algolia call on
    // the critical path of every /resources request for markup that never
    // renders. `resourcesListingProps` is kept ready for the day that grid is
    // shown unfiltered; see the note in the handover.
  ]);

  return {
    props: {
      relatedLimit: relatedLimit,
      data: ret,
      metros,
      tags: ret.field_template_category
        ? ret.field_template_category.concat(
            ret.field_template_secondary_categor
          )
        : [],
      popular_downloads_posts:
        typeof manualPopular !== 'undefined' &&
        manualPopular.length == popularLimit
          ? manualPopular
          : typeof manualPopular !== 'undefined' &&
              manualPopular.length != popularLimit
            ? manualPopular.concat(
                await new Promise((resolve, reject) => {
                  resolve(
                    popularDownloadsBlockProps(
                      endpoint,
                      lang,
                      categories,
                      popularLimit - (manualPopular ? manualPopular.length : 0),
                      'popular',
                      popularDupes
                    )
                  );
                })
              )
            : isBlockPopular(
                  getValueForPopularAndRelatedBlock(
                    ret.field_template_blocks,
                    1
                  )
                )
              ? await new Promise((resolve, reject) => {
                  resolve(
                    popularDownloadsBlockProps(
                      endpoint,
                      lang,
                      categories,
                      getValueForPopularAndRelatedBlock(
                        ret.field_template_blocks,
                        1
                      )[0]?.field_related_content_item_limit[0].value,
                      'popular',
                      popularDupes
                    )
                  );
                })
              : [],
      resource_popular_downloads_posts:
        data?.content_type === 'resources_page'
          ? await new Promise((resolve, reject) => {
              resolve(popularDownloadsProps(endpoint, lang));
            })
          : [],
      related_content:
        typeof manualRelated !== 'undefined' &&
        manualRelated.length == relatedLimit
          ? manualRelated
          : typeof manualRelated !== 'undefined' &&
              manualRelated.length != relatedLimit
            ? manualRelated.length < relatedLimit
              ? manualRelated.concat(
                  await new Promise((resolve, reject) => {
                    resolve(
                      popularDownloadsBlockProps(
                        endpoint,
                        lang,
                        categories,
                        relatedLimit - manualRelated.length,
                        'related',
                        relatedDupes
                      )
                    );
                  })
                )
              : manualRelated.slice(0, relatedLimit)
            : isBlockRelated(
                  getValueForPopularAndRelatedBlock(
                    ret.field_template_blocks,
                    0
                  )
                )
              ? await new Promise((resolve, reject) => {
                  resolve(
                    popularDownloadsBlockProps(
                      endpoint,
                      lang,
                      categories,
                      relatedLimit,
                      'related',
                      relatedDupes
                    )
                  );
                })
              : [],
      urlAlias: urlAlias,
      menuData: menuData,
      lang: lang,
      type: type,
      footerData: footerData,
      globalMetrics: globalMetrics,
      countryData: countryData?.length > 0 ? countryData : [],
      endpoint,
      dataCenters: dataCenters,
      languageSelectorData: languageSelectorData,
    },
  };
}

const getAllCategories = (first, second, third) => {
  let tids = '';
  let wasFirst = false;

  first?.forEach((item, index) => {
    if (!item) {
      return;
    }
    if (!wasFirst) {
      wasFirst = true;
      tids += item.tid;
      return;
    }

    tids += ',' + item.tid;
  });

  second?.forEach((item, index) => {
    if (!item) {
      return;
    }
    if (!wasFirst) {
      wasFirst = true;
      tids += item.tid;
      return;
    }
    tids += ',' + item.tid;
  });

  third?.forEach((item, index) => {
    if (!item) {
      return;
    }
    if (!wasFirst) {
      wasFirst = true;
      tids += item.tid;
      return;
    }
    tids += ',' + item.tid;
  });

  return tids;
};

export function getLanguage(locale) {
  const map = {
    en: 'en',
    'en-gb': 'en-gb',
    de: 'de',
    fr: 'fr',
    es: 'es',
    nl: 'nl',
    zh: 'zh-hans',
    ko: 'ko',

    asia: 'asia',
    at: 'at',
    ch: 'ch',
    se: 'se',
    dk: 'dk',
    ie: 'ie',
    be: 'be',
    el: 'el',
    it: 'it',
  };

  return map[locale] || 'en';
}

export const allMetrosProps = async (endpoint, lang) => {
  // const res = await fetch(
  //   endpoint.apiV2Url + `url-aliasv2?alias=/data-centers&langcode=${lang}`,
  //   Constants.headers
  // );
  // let data = await res.json();
  const data = await fetchDataApiV2(
    endpoint,
    `url-aliasv2?alias=/data-centers&langcode=${lang}`
  );
  return data?.metros;
};

export const allPartners = async (endpoint, lang) => {
  const res = await fetch(
    endpoint.apiV2Url + `url-aliasv2?alias=/dlr-partners-api&langcode=${lang}`,
    Constants.headers
  );
  let data = await res.json();
  return data;
};

export const smoothScroll = () => {
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

export const smoothScrollToTopOfFilters = () => {
  if (typeof window !== 'undefined') {
    let filters = document.querySelector('.filter-container');
    const y = filters.getBoundingClientRect().top + window.pageYOffset - 157;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }
};

export function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    // only execute all the code below in client side
    if (typeof window !== 'undefined') {
      // Handler to call on window resize
      function handleResize() {
        // Set window width/height to state
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }

      // Add event listener
      window.addEventListener('resize', handleResize);

      // Call handler right away so state gets updated with initial window size
      handleResize();

      // Remove event listener on cleanup
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []); // Empty array ensures that effect is only run on mount
  return windowSize;
}

const returnProps = () => {
  return {
    props: {
      data: [],
      tags: [],
      popular_downloads_posts: [],
      resource_popular_downloads_posts: [],
      related_content: [],
      urlAlias: [],
      menuData: [],
      lang: [],
      type: 0,
      footerData: [],
      globalMetrics: [],
      languageSelectorData: [],
    },
  };
};

export async function partnerServicesProp(endpoint, lang) {
  return await fetchData(endpoint, 'partner-categories?lang=' + lang);
}

export async function topicsProps(endpoint, lang) {
  return await fetchData(endpoint, 'topics-categories?lang=' + lang);
}

// Articles per page on the Blog listing. Mirrors `hitsPerPage` in
// LatestArticles — the two have to agree or the server would render a
// different slice than the client expects.
export const LATEST_ARTICLES_PER_PAGE = 9;

/**
 * How many items to request, and from where.
 *
 * `?page=N` renders page N and nothing else: `?page=3` is items 19–27, not
 * items 1–27.
 *
 * This was cumulative at first, to match what a visitor sees after clicking
 * Show More three times. SEO asked for discrete pages instead (2026-10-12):
 * these URLs exist to be crawled, and repeating every earlier page inside each
 * one multiplies the internal links on the page and dilutes what each is worth.
 *
 * The trade is reload fidelity — someone who clicks Show More three times and
 * then reloads gets page three alone rather than all three. That was raised and
 * accepted: deep pages are reached by link, not by clicking.
 *
 * Two upsides come with it: the markup stays one page in size whatever the
 * depth (the Blog's cumulative page ten was ~1.6MB and timed out), and the
 * server slice now matches what the client requests for the same page number.
 */
const discreteRequest = (page, perPage) => {
  const safePage = Math.max(0, page);

  return {
    count: perPage,
    from: safePage * perPage,
    // Algolia's offset is `page * hitsPerPage`, and hitsPerPage is now the page
    // size, so the page number passes straight through.
    algoliaPage: safePage,
  };
};

// Page sizes for the Algolia-backed listings. Each mirrors the `hitsPerPage`
// in its component; if one drifts, the server renders a different slice than
// the client and the pagination links point at the wrong pages.
export const RESOURCES_PER_PAGE = 16;
// 16, matching `hitsPerPage` in press-releases.js. This said 9, so the server
// rendered items 10–18 for `?page=2` while the client showed 17–32 — different
// content under one URL, and six releases that no page reached.
export const PRESS_RELEASES_PER_PAGE = 16;
export const PARTNERS_PER_PAGE = 8;

/**
 * One Algolia search, executed on the server.
 *
 * The URL is built by the very same helper the client uses, so there is one
 * definition of what a listing query is rather than a server copy that can
 * drift. Those helpers are pure apart from `getAlgoliaAnonymousToken()`, which
 * reads a cookie and simply returns '' here — the token only feeds click
 * analytics, which has nothing to attribute on a server render anyway.
 *
 * Returns null on any failure: the client-side fetch still runs, so a slow or
 * unavailable Algolia costs the server-rendered copy, never the page.
 */
async function fetchAlgoliaListing(url) {
  if (!url) {
    return null;
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 4000);

  try {
    const res = await fetch(url, {
      ...algoliaOptions,
      signal: controller.signal,
    });

    if (!res.ok) {
      return null;
    }

    const data = await res.json();

    return Array.isArray(data?.hits) ? data : null;
  } catch (error) {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

// Unfiltered listing queries. Only pagination is server-rendered — filters stay
// client-side and are deliberately NOT exposed as crawlable links, or every
// filter combination would become its own crawlable URL.
const emptyFilters = () => ({ lngArray: [], facetFiltersArray: [], facetFilters: [] });

export async function resourcesListingProps(host, lang, page) {
  const { lngArray, facetFiltersArray, facetFilters } = emptyFilters();
  const request = discreteRequest(page, RESOURCES_PER_PAGE);
  const dataset = getAlgoliaDatasetValue(0) + getAlgoliaIndex(host);

  return fetchAlgoliaListing(
    createFilterDataForResources(
      lngArray,
      facetFiltersArray,
      facetFilters,
      '',
      request.count,
      request.algoliaPage,
      lang,
      dataset
    )
  );
}

export async function pressReleasesListingProps(host, lang, page) {
  const { lngArray, facetFiltersArray, facetFilters } = emptyFilters();
  const request = discreteRequest(page, PRESS_RELEASES_PER_PAGE);
  const dataset = getAlgoliaDatasetValue(3) + getAlgoliaIndex(host);

  // `localFilter` undefined and language 'en' matches the default list the
  // component shows before anyone touches the local/global toggle.
  return fetchAlgoliaListing(
    createFilterPressReleases(
      lngArray,
      facetFiltersArray,
      facetFilters,
      '',
      request.count,
      request.algoliaPage,
      'en',
      undefined,
      dataset
    )
  );
}

// Page size on the Product Family listing — mirrors `hitsPerPage` there.
export const PRODUCT_FAMILY_PER_PAGE = 8;

/**
 * The Product Family listing picks its Algolia index per page: a content author
 * chooses it with `field_choose_algolia_dataset` on the block itself, so unlike
 * the other listings the dataset cannot be hardcoded here and has to be read
 * out of the block before the query can be built.
 */
export async function productFamilyListingProps(host, lang, page, block) {
  const { lngArray, facetFiltersArray, facetFilters } = emptyFilters();
  const request = discreteRequest(page, PRODUCT_FAMILY_PER_PAGE);

  const chosen = block?.field_choose_algolia_dataset?.[0]?.value;
  const dataset = getAlgoliaDatasetValue(chosen) + getAlgoliaIndex(host);

  return fetchAlgoliaListing(
    createFilterDataProductFamily(
      lngArray,
      facetFiltersArray,
      facetFilters,
      '',
      request.count,
      request.algoliaPage,
      lang,
      dataset,
      []
    )
  );
}

// Page size on the global search page — mirrors `hitsPerPage` there.
export const GLOBAL_SEARCH_PER_PAGE = 16;

/**
 * Server-renders a page of global search results.
 *
 * Rendering them is not the same as inviting search engines to index them —
 * those are separate concerns, settled by `noindex` rather than by withholding
 * SSR. What this buys is a search results page that survives a reload, works
 * without JavaScript and paints in one pass instead of two.
 *
 * Skipped entirely when there is no term: an empty search has nothing to
 * render and would cost an Algolia call on every visit to the bare page.
 */
export async function globalSearchListingProps(host, lang, page, term) {
  if (!term) {
    return null;
  }

  const { lngArray, facetFiltersArray, facetFilters } = emptyFilters();
  const request = discreteRequest(page, GLOBAL_SEARCH_PER_PAGE);

  return fetchAlgoliaListing(
    createFilterData(
      lngArray,
      facetFiltersArray,
      facetFilters,
      term,
      request.count,
      request.algoliaPage,
      lang,
      'global' + getAlgoliaIndex(host)
    )
  );
}

export async function partnersListingProps(host, lang, page) {
  const { lngArray, facetFiltersArray, facetFilters } = emptyFilters();
  const request = discreteRequest(page, PARTNERS_PER_PAGE);
  const dataset = getAlgoliaDatasetValue(4) + getAlgoliaIndex(host);

  return fetchAlgoliaListing(
    createFilterDataPartners(
      lngArray,
      facetFiltersArray,
      facetFilters,
      '',
      request.count,
      request.algoliaPage,
      lang,
      dataset,
      undefined
    )
  );
}

/**
 * Fetches one page of blog articles on the server.
 *
 * The listing used to be built entirely in a `useEffect`, so a request for
 * /resources/blog came back with no articles and no "See More" control at all —
 * nothing for a search engine to follow, which is what orphaned the rest of the
 * series. Rendering the requested slice here puts both the cards and the link
 * to the next page into the source HTML.
 *
 * Deliberately not routed through `fetchDataApiV2`: on a cache hit that helper
 * returns early unless the payload carries `node_id` and `content_type`, which
 * a plain array of articles never does — so the second request for any given
 * page would come back `undefined`.
 *
 * Returns null on any failure. The client-side fetch still runs, so a slow or
 * unavailable CMS costs us the server-rendered copy, never the page itself.
 */
export async function latestArticlesProps(endpoint, lang, page, author = '') {
  // Discrete, like every other listing here: `?page=4` is articles 28 to 36.
  const { count, from } = discreteRequest(page, LATEST_ARTICLES_PER_PAGE);

  // One article more than the page shows, so the page knows for certain
  // whether a next page exists. Judging by "this page is full" linked an
  // exactly-full last page to an empty one — the Blog's `?page=25` pointed
  // rel="next" at a `?page=26` with no articles.
  const url =
    `${endpoint.apiV2Url}articles?langcode=${lang}&filter=recent` +
    `&from=${from}&count=${count + 1}&topics=` +
    `&author=${encodeURIComponent(author)}&search=&tags=`;

  const controller = new AbortController();
  // SSR sits on the critical path for the whole page. If the articles endpoint
  // is slow, drop the server-rendered listing rather than hold up the response.
  const timeout = setTimeout(() => controller.abort(), 4000);

  try {
    const res = await fetch(url, { signal: controller.signal });

    if (!res.ok) {
      return null;
    }

    const articles = await res.json();

    if (!Array.isArray(articles)) {
      return null;
    }

    return {
      articles: articles.slice(0, count),
      hasMore: articles.length > count,
    };
  } catch (error) {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}
